import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Edit, Trash2, MoreVertical, Users, Clock, FileText, Video, Music, Image, Archive, File, Eye, EyeOff } from "lucide-react";
import { UnifiedService } from "@/hooks/useIntegratedServices";

interface UnifiedServiceCardProps {
  service: UnifiedService;
  onEdit?: (service: UnifiedService) => void;
  onDelete?: (id: string, type: 'mentoria' | 'conteudo') => void;
  onToggleActive?: (id: string, isActive: boolean, type: 'mentoria' | 'conteudo') => void;
}

const CONTENT_TYPE_ICONS = {
  pdf: FileText,
  video: Video,
  audio: Music,
  image: Image,
  zip: Archive,
  document: File
};

const CONTENT_TYPE_LABELS = {
  pdf: 'PDF',
  video: 'Vídeo',
  audio: 'Áudio',
  image: 'Imagem',
  zip: 'ZIP',
  document: 'Documento'
};

export const UnifiedServiceCard: React.FC<UnifiedServiceCardProps> = ({
  service,
  onEdit,
  onDelete,
  onToggleActive
}) => {
  const formatPrice = (cents: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(cents / 100);
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h${mins > 0 ? ` ${mins}min` : ''}`;
    }
    return `${mins}min`;
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit(service);
    }
  };

  const handleDelete = () => {
    if (onDelete && confirm(`Tem certeza que deseja excluir ${service.type === 'mentoria' ? 'esta mentoria' : 'este conteúdo'}? Esta ação não pode ser desfeita.`)) {
      onDelete(service.id, service.type);
    }
  };

  const handleToggleActive = () => {
    if (onToggleActive) {
      onToggleActive(service.id, !service.is_active, service.type);
    }
  };

  const getTypeIcon = () => {
    if (service.type === 'mentoria') {
      return service.is_group ? Users : Clock;
    } else {
      const contentType = service.content_type as keyof typeof CONTENT_TYPE_ICONS;
      return CONTENT_TYPE_ICONS[contentType] || FileText;
    }
  };

  const getTypeLabel = () => {
    if (service.type === 'mentoria') {
      return service.is_group 
        ? `Mentoria em Grupo (${service.max_participants} max)`
        : 'Mentoria Individual';
    } else {
      const contentType = service.content_type as keyof typeof CONTENT_TYPE_LABELS;
      return CONTENT_TYPE_LABELS[contentType] || 'Conteúdo Digital';
    }
  };

  const TypeIcon = getTypeIcon();

  return (
    <Card className={`bg-[#1E293B] border-[#2A3749] transition-all duration-200 hover:border-[#00B6F0] ${!service.is_active ? 'opacity-60' : ''}`}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2 flex-1">
            <div className="p-2 bg-[#242C37] rounded-lg">
              <TypeIcon className="h-5 w-5 text-[#00B6F0]" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Badge 
                  variant="outline" 
                  className={`text-xs ${service.type === 'mentoria' ? 'border-blue-500 text-blue-400' : 'border-green-500 text-green-400'}`}
                >
                  {service.type === 'mentoria' ? 'Mentoria' : 'Conteúdo'}
                </Badge>
                {!service.is_active && (
                  <Badge variant="outline" className="border-gray-500 text-gray-400">
                    Inativo
                  </Badge>
                )}
              </div>
              <CardTitle className="text-white text-lg leading-tight">
                {service.title}
              </CardTitle>
            </div>
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#242C37] border-[#2A3749] text-white">
              <DropdownMenuItem 
                onClick={handleEdit}
                className="hover:bg-[#2A3749] cursor-pointer"
              >
                <Edit className="mr-2 h-4 w-4" />
                Editar
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={handleToggleActive}
                className="hover:bg-[#2A3749] cursor-pointer"
              >
                {service.is_active ? (
                  <>
                    <EyeOff className="mr-2 h-4 w-4" />
                    Desativar
                  </>
                ) : (
                  <>
                    <Eye className="mr-2 h-4 w-4" />
                    Ativar
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={handleDelete}
                className="hover:bg-red-600 cursor-pointer text-red-400 hover:text-white"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Excluir
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      
      <CardContent>
        {/* Imagem de Capa */}
        {service.cover_image_url && (
          <div className="mb-4">
            <img
              src={service.cover_image_url}
              alt={service.title}
              className="w-full h-32 object-cover rounded-lg"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
        )}
        
        {/* Descrição */}
        <CardDescription className="text-gray-300 mb-4 line-clamp-3">
          {service.description}
        </CardDescription>
        
        {/* Informações específicas */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <TypeIcon className="h-4 w-4" />
            <span>{getTypeLabel()}</span>
          </div>
          
          {service.type === 'mentoria' && service.duration_minutes && (
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Clock className="h-4 w-4" />
              <span>Duração: {formatDuration(service.duration_minutes)}</span>
            </div>
          )}
          
          {service.game && (
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <span className="w-4 h-4 bg-[#00B6F0] rounded-full flex-shrink-0"></span>
              <span>Jogo: {service.game.name}</span>
            </div>
          )}
        </div>
        
        {/* Preço */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#2A3749]">
          <div className="text-2xl font-bold text-[#00B6F0]">
            {formatPrice(service.price_cents)}
          </div>
          <div className="text-xs text-gray-400">
            {service.type === 'mentoria' ? 'por sessão' : 'compra única'}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}; 